import { useState, type ReactNode } from "react";
import classNames from "classnames";
import { IconMore } from "@icons/index";

export type ReferenceVideoCardVariant = "generated" | "uploaded" | "saved";

export interface ReferenceVideoLibraryItem {
  id: string;
  variant: ReferenceVideoCardVariant;
  coverUrl?: string;
  videoUrl?: string;
  productImage?: string;
  productTitle?: string;
  raw: unknown;
}

interface BaseReferenceVideoCardProps {
  video: ReferenceVideoLibraryItem;
  onClick: () => void;
}

interface GeneratedReferenceVideoCardProps extends BaseReferenceVideoCardProps {}

export const GeneratedReferenceVideoCard = ({ video, onClick }: GeneratedReferenceVideoCardProps) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className="relative flex h-[270px] w-full cursor-pointer flex-col overflow-hidden rounded-[8px] border border-kds-border bg-kds-bg transition-colors hover:border-kds-accent"
    >
      <div className="h-[210px] w-full overflow-hidden bg-kds-bg-subtle">
        {video.coverUrl && (
          <img src={video.coverUrl} alt="" className="h-full w-full object-cover pointer-events-none" />
        )}
      </div>
      <div className="mx-[12px] my-[12px] flex h-[36px] items-center gap-[8px]">
        <div className="size-[32px] shrink-0 overflow-hidden rounded-[4px] border border-kds-border bg-kds-bg-subtle">
          {video.productImage && (
            <img
              src={video.productImage}
              alt=""
              className="h-full w-full rounded-[4px] object-cover pointer-events-none"
            />
          )}
        </div>
        <p className="line-clamp-2 min-w-0 flex-1 text-left text-[13px] leading-[1.4] text-kds-fg-subtle">
          {video.productTitle}
        </p>
      </div>
    </button>
  );
};

interface ReferenceVideoThumbnailCardProps extends BaseReferenceVideoCardProps {
  showMoreAction?: boolean;
  onMoreClick?: () => void;
  overlay?: ReactNode;
}

export const ReferenceVideoThumbnailCard = ({
  video,
  onClick,
  showMoreAction = false,
  onMoreClick,
  overlay,
}: ReferenceVideoThumbnailCardProps) => {
  const [isPortrait, setIsPortrait] = useState(true);
  const mediaUrl = video.videoUrl ?? video.coverUrl;
  const hasVideo = Boolean(video.videoUrl);

  return (
    <button
      type="button"
      onClick={onClick}
      className="relative flex h-[270px] w-full cursor-pointer items-center justify-center overflow-hidden rounded-[8px] border border-kds-border bg-(--palette-slate-5) transition-colors hover:border-kds-accent"
    >
      {hasVideo ? (
        <video
          src={video.videoUrl}
          muted
          poster={(video.raw as any)?.coverImageUrl}
          preload="metadata"
          playsInline
          onLoadedMetadata={(event) => {
            const target = event.currentTarget;
            setIsPortrait(target.videoHeight >= target.videoWidth);
          }}
          className={classNames("pointer-events-none h-full w-full", isPortrait ? "object-cover" : "object-contain")}
        />
      ) : (
        mediaUrl && <img src={mediaUrl} alt="" className="pointer-events-none h-full w-full object-cover" />
      )}
      {showMoreAction && (
        <span
          className="absolute right-[12px] top-[12px] flex size-[24px] items-center justify-center rounded-[4px] bg-kds-mask text-kds-light"
          onClick={(event) => {
            event.stopPropagation();
            onMoreClick?.();
          }}
        >
          <IconMore className="size-[16px]" />
        </span>
      )}
      {overlay}
    </button>
  );
};
